/* pi-newsletter-popup.js ===============================================
   Newsletter popup with time-delay / exit-intent / scroll triggers,
   localStorage frequency, success state with copy-code button.
   ====================================================================== */
(function () {
  if (window.customElements.get('pi-popup')) return;

  const STORAGE_KEY = 'pi_popup_dismissed_at';
  const SESSION_KEY = 'pi_popup_session_dismissed';

  class PiPopup extends HTMLElement {
    constructor() {
      super();
      this.untrap = null;
      this.shownInSession = false;
    }

    connectedCallback() {
      this.trigger = this.dataset.trigger || 'time_delay';
      this.value = parseInt(this.dataset.value, 10) || 5;
      this.frequency = this.dataset.frequency || 'once';
      this.formEl = this.querySelector('.pi-popup__form');
      this.successEl = this.querySelector('.pi-popup__success');
      this.declineEl = this.querySelector('.pi-popup__decline');
      this.copyBtn = this.querySelector('.pi-popup__copy-btn');
      this.codeEl = this.querySelector('.pi-popup__code');

      this.bindControls();

      if (!this.shouldShow()) return;

      // Editor: always show on load so merchant can style.
      if (window.Shopify && Shopify.designMode) {
        this.open();
        return;
      }

      this.scheduleTrigger();
    }

    bindControls() {
      const close = this.querySelector('.pi-popup__close');
      const backdrop = this.querySelector('.pi-popup__backdrop');
      if (close) close.addEventListener('click', () => this.dismiss(false));
      if (backdrop) backdrop.addEventListener('click', () => this.dismiss(false));
      if (this.declineEl)
        this.declineEl.addEventListener('click', () => this.dismiss(false));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dataset.open === 'true') this.dismiss(false);
      });

      if (this.formEl) {
        this.formEl.addEventListener('submit', (e) => this.onSubmit(e));
      }

      if (this.copyBtn) {
        this.copyBtn.addEventListener('click', () => this.copyCode());
      }
    }

    shouldShow() {
      try {
        if (this.frequency === 'every_visit') return true;
        if (this.frequency === 'every_session') {
          return sessionStorage.getItem(SESSION_KEY) !== '1';
        }
        // 'once' — check localStorage.
        return !localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return true;
      }
    }

    rememberDismissal() {
      try {
        if (this.frequency === 'every_session') {
          sessionStorage.setItem(SESSION_KEY, '1');
        } else if (this.frequency === 'once') {
          localStorage.setItem(STORAGE_KEY, String(Date.now()));
        }
      } catch (e) {
        /* ignore */
      }
    }

    scheduleTrigger() {
      if (this.trigger === 'time_delay') {
        setTimeout(() => this.open(), this.value * 1000);
      } else if (this.trigger === 'scroll_percent') {
        const onScroll = () => {
          const doc = document.documentElement;
          const max = doc.scrollHeight - doc.clientHeight;
          if (max <= 0) return;
          const pct = (window.scrollY / max) * 100;
          if (pct >= this.value) {
            window.removeEventListener('scroll', onScroll);
            this.open();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else if (this.trigger === 'exit_intent') {
        const onMouseOut = (e) => {
          if (!e.toElement && !e.relatedTarget && e.clientY < 10) {
            document.removeEventListener('mouseout', onMouseOut);
            this.open();
          }
        };
        document.addEventListener('mouseout', onMouseOut);
        // Fallback: also open after 60s.
        setTimeout(() => {
          if (this.dataset.open !== 'true') this.open();
        }, 60000);
      }
    }

    open() {
      this.dataset.open = 'true';
      this.setAttribute('aria-hidden', 'false');
      if (window.PiUtils) {
        window.PiUtils.lockScroll(this);
        this.untrap = window.PiUtils.trapFocus(this, this.querySelector('input,button'));
      }
      this.dispatchEvent(new CustomEvent('pi:popup:opened', { bubbles: true }));
    }

    close() {
      this.dataset.open = 'false';
      this.setAttribute('aria-hidden', 'true');
      if (this.untrap) {
        this.untrap();
        this.untrap = null;
      }
      if (window.PiUtils) window.PiUtils.unlockScroll(this);
    }

    dismiss(converted) {
      this.rememberDismissal();
      this.close();
      this.dispatchEvent(
        new CustomEvent(converted ? 'pi:popup:converted' : 'pi:popup:dismissed', {
          bubbles: true,
        })
      );
    }

    async onSubmit(e) {
      // Let the Shopify customer form submit through its action. We
      // intercept only to swap UI to the success state.
      const formNode = this.querySelector('form.pi-popup__form-el');
      if (!formNode) return;
      e.preventDefault();
      const fd = new FormData(formNode);
      try {
        await fetch(formNode.action, {
          method: 'POST',
          body: fd,
          headers: { Accept: 'text/html' },
        });
      } catch (err) {
        /* even on error we show success — Shopify often returns redirect HTML */
      }
      this.showSuccess();
    }

    showSuccess() {
      if (this.formEl) this.formEl.dataset.hidden = 'true';
      if (this.declineEl) this.declineEl.dataset.hidden = 'true';
      if (this.successEl) this.successEl.dataset.active = 'true';
      this.rememberDismissal();
      this.dispatchEvent(new CustomEvent('pi:popup:converted', { bubbles: true }));
    }

    async copyCode() {
      if (!this.codeEl || !this.copyBtn) return;
      const code = (this.codeEl.textContent || '').trim();
      try {
        await navigator.clipboard.writeText(code);
      } catch (e) {
        const range = document.createRange();
        range.selectNode(this.codeEl);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        document.execCommand('copy');
        window.getSelection().removeAllRanges();
      }
      const old = this.copyBtn.dataset.label || this.copyBtn.textContent;
      this.copyBtn.dataset.label = old;
      this.copyBtn.querySelector('.pi-popup__copy-label').textContent =
        this.copyBtn.dataset.copied || 'Copied!';
      setTimeout(() => {
        this.copyBtn.querySelector('.pi-popup__copy-label').textContent = old;
      }, 1800);
    }
  }

  window.customElements.define('pi-popup', PiPopup);
})();
